
import React from 'react';
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { MIN_WORD_COUNT, countWords } from './text-utils';

interface JobDescriptionInputProps {
  jobDescription: string;
  onJobDescriptionChange: (value: string) => void;
  onGenerateProposal: () => void;
  isLoading: boolean;
  isRateLimited: boolean;
  cooldownRemaining: number;
  buttonText?: string;
}

const JobDescriptionInput: React.FC<JobDescriptionInputProps> = ({
  jobDescription,
  onJobDescriptionChange,
  onGenerateProposal,
  isLoading,
  isRateLimited,
  cooldownRemaining,
  buttonText = "Generate Winning Proposal"
}) => {
  const wordCount = countWords(jobDescription);
  const isDisabled = isLoading || !jobDescription.trim() || isRateLimited || wordCount < MIN_WORD_COUNT;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Job Description</CardTitle>
        <CardDescription>
          Paste the job description you're applying for, and we'll generate a winning proposal. Minimum {MIN_WORD_COUNT} words required.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="jobDescription">Job Description</Label>
            <Textarea
              id="jobDescription"
              placeholder="Paste the job description here... (min. 25 words)"
              className="min-h-[200px] bg-slate-100 dark:bg-slate-800"
              value={jobDescription}
              onChange={(e) => onJobDescriptionChange(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Word count: {wordCount} 
              {wordCount < MIN_WORD_COUNT && (
                <span className="text-destructive"> (need at least {MIN_WORD_COUNT} words)</span>
              )}
            </p>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          onClick={onGenerateProposal} 
          disabled={isDisabled}
          className="w-full"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : isRateLimited ? (
            `Wait ${Math.ceil(cooldownRemaining / 1000)}s before generating`
          ) : (
            buttonText
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default JobDescriptionInput;
